import { fetchImageAsBase64 } from '@geneo2-web/shared-ui';
import html2canvas from 'html2canvas';
import HTMLtoDOCX from 'html-to-docx';
import JSZip from 'jszip';

/** Max width for question/content images in DOCX (px). Keep modest so Word doesn't blow them up. */
const DOCX_MAX_CONTENT_IMAGE_WIDTH_PX = 320;
/** Max width for rasterized KaTeX images (px) */
const DOCX_MAX_KATEX_IMAGE_WIDTH_PX = 520;
/** Max width for rasterized nested content tables (px) */
const DOCX_MAX_TABLE_IMAGE_WIDTH_PX = 520;
/** Fallback EMUs when html-to-docx writes empty wp:extent (190500 ≈ 0.21") */
const DOCX_FALLBACK_EXTENT_EMU = 190500;
/** Fallback body font (matches QPG print default). html-to-docx HIP = px * 1.5 */
export const DOCX_DEFAULT_FONT_SIZE_PX = 16;
const EMU_PER_POINT = 12700;
const pxToHip = (px: number) => Math.round(px * 1.5);
const DOCX_MATH_INLINE_ALT = 'docx-math-inline';
const DOCX_MATH_BLOCK_ALT = 'docx-math-block';
const DOCX_NESTED_TABLE_ALT = 'docx-content-table';

const DOCX_PHRASING_TAGS = new Set([
  'SPAN',
  'IMG',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'SUB',
  'SUP',
  'A',
  'CODE',
  'MARK',
  'SMALL',
  'LABEL',
]);

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const SUPPORTED_DATA_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp);/i;

/**
 * Convert remote/relative <img> srcs in the container to base64 data URLs so PDF
 * (html2canvas) and DOC share the same embedded images. Tries current src first,
 * then data-export-src (e.g. signed URL) if present. Leaves the tag unchanged if
 * all fetches fail.
 */
export const inlineRemoteImagesAsBase64 = async (
  container: HTMLElement
): Promise<void> => {
  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const currentSrc = (img.getAttribute('src') || '').trim();
      if (SUPPORTED_DATA_IMAGE_RE.test(currentSrc)) return;

      const exportSrc = (img.getAttribute('data-export-src') || '').trim();
      const candidates = [currentSrc, exportSrc].filter(
        (src, index, arr) => !!src && arr.indexOf(src) === index
      );

      for (const candidate of candidates) {
        try {
          const absoluteUrl = new URL(candidate, window.location.origin).href;
          const base64 = await fetchImageAsBase64(absoluteUrl);
          if (base64 && SUPPORTED_DATA_IMAGE_RE.test(base64)) {
            img.setAttribute('src', base64);
            return;
          }
        } catch {
          // try next candidate
        }
      }
    })
  );
};

const loadImageDimensions = (
  src: string
): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Inline DrawingML images sit on the text baseline. Raise/lower each math run with
 * w:position so the image is vertically centered against the body font size.
 */
const centerInlineMathRuns = (xml: string, fontSizePx: number): string => {
  const fontSizePts = (fontSizePx * 72) / 96;
  return xml.replace(/<w:r\b[^>]*>[\s\S]*?<\/w:r>/g, (run) => {
    if (!run.includes(DOCX_MATH_INLINE_ALT)) return run;
    if (run.includes('<w:position ')) return run;
    const cyMatch = run.match(/<wp:extent[^>]*\bcy="(\d+)"/);
    if (!cyMatch) return run;
    const imageHeightPts = Number(cyMatch[1]) / EMU_PER_POINT;
    // shift_pts = (font - image) / 2 → half-points = fontPts - imagePts
    const positionHp = Math.round(fontSizePts - imageHeightPts);
    if (positionHp === 0) return run;
    if (run.includes('<w:rPr>')) {
      return run.replace(
        '<w:rPr>',
        `<w:rPr><w:position w:val="${positionHp}"/>`
      );
    }
    return run.replace(
      /(<w:r\b[^>]*>)/,
      `$1<w:rPr><w:position w:val="${positionHp}"/></w:rPr>`
    );
  });
};

/**
 * html-to-docx writes empty <wp:extent/> when image size is unknown — Word Desktop
 * refuses to open those files. Also move <w:sectPr> to end of body (library puts it first).
 */
const repairDocxForWord = async (
  input: Blob | ArrayBuffer | Uint8Array,
  fontSizePx: number = DOCX_DEFAULT_FONT_SIZE_PX
): Promise<Blob> => {
  const zip = await JSZip.loadAsync(input as Blob);
  const docFile = zip.file('word/document.xml');
  if (docFile) {
    let xml = await docFile.async('string');
    xml = xml.replace(
      /<wp:extent\s*\/>/g,
      `<wp:extent cx="${DOCX_FALLBACK_EXTENT_EMU}" cy="${DOCX_FALLBACK_EXTENT_EMU}"/>`
    );
    xml = xml.replace(
      /<a:ext\s*\/>/g,
      `<a:ext cx="${DOCX_FALLBACK_EXTENT_EMU}" cy="${DOCX_FALLBACK_EXTENT_EMU}"/>`
    );
    xml = centerInlineMathRuns(xml, fontSizePx);
    // Move section properties to the end of <w:body> (Word expects this)
    const sectMatch = xml.match(/\s*<w:sectPr>[\s\S]*?<\/w:sectPr>/);
    if (sectMatch) {
      const withoutSect = xml.replace(sectMatch[0], '');
      if (withoutSect.includes('</w:body>')) {
        xml = withoutSect.replace('</w:body>', `${sectMatch[0]}\n  </w:body>`);
      }
    }
    zip.file('word/document.xml', xml);
  }

  // html-to-docx hard-codes `w:after="120"` in the default paragraph props, which
  // adds a gap after every paragraph. Add `w:contextualSpacing` so Word does not
  // add space between consecutive paragraphs of the same style.
  const stylesFile = zip.file('word/styles.xml');
  if (stylesFile) {
    let stylesXml = await stylesFile.async('string');
    if (!stylesXml.includes('w:contextualSpacing')) {
      stylesXml = stylesXml.replace(
        /(<w:pPrDefault>\s*<w:pPr>\s*<w:spacing[^>]*\/>)/,
        '$1<w:contextualSpacing/>'
      );
    }
    zip.file('word/styles.xml', stylesXml);
  }

  const out = await zip.generateAsync({
    type: 'blob',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });
  return out;
};

const waitForImagesToLoad = async (container: HTMLElement) => {
  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) return resolve();
          img.onload = img.onerror = () => resolve();
        })
    )
  );
};

/**
 * html-to-docx cannot use KaTeX CSS/fonts. Rasterize each rendered .katex
 * (from GeneoKatex) into a PNG <img> so math survives in Word.
 */
const convertKatexElementsToImages = async (root: HTMLElement) => {
  const hosts = (
    Array.from(root.querySelectorAll('.katex-display, .katex')) as HTMLElement[]
  ).filter((el) => {
    // Prefer capturing .katex-display as a whole; skip nested .katex inside it.
    if (el.classList.contains('katex') && el.closest('.katex-display')) {
      return false;
    }
    // Skip .katex nested inside another .katex
    if (el.classList.contains('katex') && el.parentElement?.closest('.katex')) {
      return false;
    }
    return true;
  });

  const scale = 2;
  for (const host of hosts) {
    try {
      const isDisplay =
        host.classList.contains('katex-display') ||
        !!host.closest('.katex-genius-content-center');

      // Capture via a padded wrapper so superscripts/fractions aren't clipped.
      const wrap = document.createElement('span');
      wrap.style.display = 'inline-block';
      wrap.style.padding = '1px 2px';
      wrap.style.margin = '0';
      wrap.style.lineHeight = 'normal';
      wrap.style.overflow = 'visible';
      wrap.style.backgroundColor = '#ffffff';
      wrap.style.verticalAlign = 'middle';
      host.parentNode?.insertBefore(wrap, host);
      wrap.appendChild(host);

      const canvas = await html2canvas(wrap, {
        backgroundColor: '#ffffff',
        scale,
        logging: false,
        useCORS: true,
        onclone: (_doc, cloned) => {
          const el = cloned as HTMLElement;
          el.style.overflow = 'visible';
          el.querySelectorAll<HTMLElement>('*').forEach((node) => {
            node.style.overflow = 'visible';
          });
        },
      });
      const dataUrl = canvas.toDataURL('image/png');
      if (!SUPPORTED_DATA_IMAGE_RE.test(dataUrl)) {
        wrap.replaceWith(host);
        continue;
      }

      // Display size from canvas (not getBoundingClientRect — that often clips KaTeX).
      let w = Math.max(1, Math.round(canvas.width / scale));
      let h = Math.max(1, Math.round(canvas.height / scale));
      if (w > DOCX_MAX_KATEX_IMAGE_WIDTH_PX) {
        const shrink = DOCX_MAX_KATEX_IMAGE_WIDTH_PX / w;
        w = DOCX_MAX_KATEX_IMAGE_WIDTH_PX;
        h = Math.max(1, Math.round(h * shrink));
      }

      const img = document.createElement('img');
      img.src = dataUrl;
      // Marker consumed by repairDocxForWord for vertical centering in Word.
      img.alt = isDisplay ? DOCX_MATH_BLOCK_ALT : DOCX_MATH_INLINE_ALT;
      img.setAttribute('data-docx-math', isDisplay ? 'block' : 'inline');
      img.setAttribute('width', String(w));
      img.setAttribute('height', String(h));
      // html-to-docx sizes from style.width/height (px), not HTML attributes alone.
      img.style.width = `${w}px`;
      img.style.height = `${h}px`;
      img.style.verticalAlign = 'middle';
      img.style.display = isDisplay ? 'block' : 'inline';
      img.style.margin = isDisplay ? '4px 0' : '0 1px';
      wrap.replaceWith(img);
    } catch (err) {
      console.warn('Failed to rasterize KaTeX node for DOCX', err);
    }
  }
};

/**
 * html-to-docx cannot nest <table> inside a <td> — nested tables are passed to
 * buildParagraph and disappear. DOCX question layout uses a table, so content
 * TableElements end up nested. Rasterize them in place (after KaTeX) so the
 * grid still appears in Word.
 */
const rasterizeNestedTablesForDocx = async (root: HTMLElement) => {
  // Document order is parents before children; reverse so deepest tables go first.
  const nestedTables = Array.from(
    root.querySelectorAll('td table, th table')
  ).reverse() as HTMLElement[];

  const scale = 2;
  for (const table of nestedTables) {
    if (!table.isConnected || !table.closest('td, th')) continue;
    try {
      table.style.borderCollapse = 'collapse';
      table.style.borderSpacing = '0';
      table.style.backgroundColor = '#ffffff';
      table.style.maxWidth = '100%';

      table.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
        const computed = window.getComputedStyle(cell);
        const hasBorder =
          (computed.borderTopWidth && computed.borderTopWidth !== '0px') ||
          (computed.borderRightWidth && computed.borderRightWidth !== '0px') ||
          (computed.borderBottomWidth && computed.borderBottomWidth !== '0px') ||
          (computed.borderLeftWidth && computed.borderLeftWidth !== '0px');
        if (!hasBorder) {
          cell.style.border = '1px solid #333333';
        }
        if (!cell.style.padding) cell.style.padding = '4px 6px';
        if (!cell.style.backgroundColor) cell.style.backgroundColor = '#ffffff';
      });

      const canvas = await html2canvas(table, {
        backgroundColor: '#ffffff',
        scale,
        logging: false,
        useCORS: true,
        onclone: (_doc, cloned) => {
          const el = cloned as HTMLElement;
          el.style.overflow = 'visible';
          el.style.borderCollapse = 'collapse';
          el.style.borderSpacing = '0';
          el.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
            if (!cell.style.border) cell.style.border = '1px solid #333333';
          });
        },
      });

      const dataUrl = canvas.toDataURL('image/png');
      if (!SUPPORTED_DATA_IMAGE_RE.test(dataUrl)) continue;

      let w = Math.max(1, Math.round(canvas.width / scale));
      let h = Math.max(1, Math.round(canvas.height / scale));
      if (w > DOCX_MAX_TABLE_IMAGE_WIDTH_PX) {
        const shrink = DOCX_MAX_TABLE_IMAGE_WIDTH_PX / w;
        w = DOCX_MAX_TABLE_IMAGE_WIDTH_PX;
        h = Math.max(1, Math.round(h * shrink));
      }

      const img = document.createElement('img');
      img.src = dataUrl;
      img.alt = DOCX_NESTED_TABLE_ALT;
      img.setAttribute('data-docx-table', '1');
      img.setAttribute('width', String(w));
      img.setAttribute('height', String(h));
      img.style.width = `${w}px`;
      img.style.height = `${h}px`;
      img.style.display = 'block';
      img.style.margin = '6px 0';
      table.replaceWith(img);
    } catch (err) {
      console.warn('Failed to rasterize nested table for DOCX', err);
    }
  }
};

/**
 * html-to-docx treats each child of a <div> as its own block. Mixed text + <img>
 * therefore wraps every KaTeX image onto a new line. Promote phrasing-only divs
 * to <p> so inline math stays in the same paragraph as surrounding text.
 */
const promotePhrasingDivsForDocx = (root: HTMLElement, fontPx: number) => {
  const divs = Array.from(root.querySelectorAll('div')).reverse();
  for (const div of divs) {
    const children = Array.from(div.childNodes);
    if (children.length === 0) continue;

    const onlyPhrasing = children.every((node) => {
      if (node.nodeType === Node.TEXT_NODE) return true;
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      return DOCX_PHRASING_TAGS.has((node as Element).tagName);
    });
    if (!onlyPhrasing) continue;

    const hasInlineContent = children.some((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').trim().length > 0;
      }
      return node.nodeType === Node.ELEMENT_NODE;
    });
    if (!hasInlineContent) continue;

    const p = document.createElement('p');
    const style = div.getAttribute('style');
    if (style) p.setAttribute('style', style);
    p.style.fontSize = `${fontPx}px`;
    p.style.lineHeight = '1.35';
    while (div.firstChild) p.appendChild(div.firstChild);
    div.replaceWith(p);
  }
};

/**
 * html-to-docx does not recurse into nested block <div>s inside a <td> — for each
 * direct child of a cell it only extracts inline runs, so a wrapper <div> (and any
 * text nested inside it) is dropped. Unwrap every <div> within table cells so the
 * cell ends up with a flat list of <p>/<img>/<ul>/<ol> that html-to-docx renders
 * correctly. Run this AFTER promotePhrasingDivsForDocx so leaf text is already in
 * <p> tags before its wrapper divs are removed.
 */
const flattenTableCellDivsForDocx = (root: HTMLElement) => {
  const cells = Array.from(root.querySelectorAll('td'));
  for (const cell of cells) {
    // Re-query after each pass because unwrapping lifts nested divs up a level.
    let divs = Array.from(cell.querySelectorAll('div'));
    let guard = 0;
    while (divs.length && guard < 100) {
      for (const div of divs) {
        const parent = div.parentNode;
        if (!parent) continue;
        while (div.firstChild) parent.insertBefore(div.firstChild, div);
        parent.removeChild(div);
      }
      divs = Array.from(cell.querySelectorAll('div'));
      guard += 1;
    }
  }
};

/** Force a uniform font size so html-to-docx doesn't keep mixed preview sizes. */
const applyDocxFontSize = (root: HTMLElement, fontPx: number) => {
  root.style.fontSize = `${fontPx}px`;
  root
    .querySelectorAll<HTMLElement>(
      'p, span, li, td, th, h1, h2, h3, h4, h5, h6, label'
    )
    .forEach((el) => {
      el.style.fontSize = `${fontPx}px`;
    });
};

const parsePx = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const m = String(value)
    .trim()
    .match(/^(\d+(?:\.\d+)?)px$/i);
  return m ? Number(m[1]) : null;
};

/**
 * html-to-docx / image-size only handle raster images with a detectable type.
 * Convert remote/relative imgs to base64 data URLs; drop anything we can't convert
 * (empty src, SVG, failed fetches) so generation doesn't throw.
 * Prefer existing display size (attrs/style) so KaTeX PNGs are not upscaled to
 * their 2x canvas natural size; cap content images so they stay readable in Word.
 */
const sanitizeHtmlImagesForDocx = async (html: string): Promise<string> => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const imgs = Array.from(wrapper.querySelectorAll('img'));

  await Promise.all(
    imgs.map(async (img) => {
      let src = (img.getAttribute('src') || '').trim();
      if (!src) {
        img.remove();
        return;
      }

      if (!SUPPORTED_DATA_IMAGE_RE.test(src)) {
        if (src.startsWith('data:')) {
          img.remove();
          return;
        }
        try {
          const absoluteUrl = new URL(src, window.location.origin).href;
          const base64 = await fetchImageAsBase64(absoluteUrl);
          if (base64 && SUPPORTED_DATA_IMAGE_RE.test(base64)) {
            img.setAttribute('src', base64);
            src = base64;
          } else {
            img.remove();
            return;
          }
        } catch {
          img.remove();
          return;
        }
      }

      const dims = await loadImageDimensions(src);
      if (!dims || dims.width <= 0 || dims.height <= 0) {
        img.remove();
        return;
      }

      const isMath = img.hasAttribute('data-docx-math');
      const isTable = img.hasAttribute('data-docx-table');
      const maxWidth = isMath
        ? DOCX_MAX_KATEX_IMAGE_WIDTH_PX
        : isTable
        ? DOCX_MAX_TABLE_IMAGE_WIDTH_PX
        : DOCX_MAX_CONTENT_IMAGE_WIDTH_PX;

      // Prefer already-chosen display size (set during KaTeX rasterize / CSS).
      const attrW = Number(img.getAttribute('width'));
      const attrH = Number(img.getAttribute('height'));
      const styleW = parsePx(img.style.width);
      const styleH = parsePx(img.style.height);

      let width =
        (styleW && styleW > 0 ? styleW : null) ||
        (attrW > 0 ? attrW : null) ||
        dims.width;
      let height =
        (styleH && styleH > 0 ? styleH : null) ||
        (attrH > 0 ? attrH : null) ||
        dims.height;

      // If only one side came from display hints, keep aspect from natural image.
      if (styleW && styleW > 0 && !(styleH && styleH > 0)) {
        height = Math.max(1, Math.round((dims.height / dims.width) * width));
      } else if (styleH && styleH > 0 && !(styleW && styleW > 0)) {
        width = Math.max(1, Math.round((dims.width / dims.height) * height));
      }

      if (width > maxWidth) {
        const shrink = maxWidth / width;
        width = maxWidth;
        height = Math.max(1, Math.round(height * shrink));
      }

      img.setAttribute('width', String(width));
      img.setAttribute('height', String(height));
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
      if (!isMath) {
        img.style.display = img.style.display || 'block';
        img.style.margin = img.style.margin || '6px 0';
      }
    })
  );

  return wrapper.innerHTML;
};

export const generateDocxFromElement = async ({
  el,
  fontSizePx = DOCX_DEFAULT_FONT_SIZE_PX,
  title,
  filename,
}: {
  el: HTMLElement;
  fontSizePx?: number;
  title: string;
  filename: string;
}) => {
  const docxFontPx =
    fontSizePx && fontSizePx > 0 ? fontSizePx : DOCX_DEFAULT_FONT_SIZE_PX;
  const docxFontHip = pxToHip(docxFontPx);
  applyDocxFontSize(el, docxFontPx);
  await new Promise((r) => requestAnimationFrame(r));

  // GeneoKatex renders CSS-based HTML; Word can't use that — rasterize first.
  await convertKatexElementsToImages(el);
  // Content tables nested in the question layout <td> are dropped by
  // html-to-docx; capture them as images while math is already rasterized.
  await rasterizeNestedTablesForDocx(el);
  await waitForImagesToLoad(el);
  // Keep text + inline math in one paragraph (html-to-docx splits div children).
  promotePhrasingDivsForDocx(el, docxFontPx);
  // Flatten wrapper divs inside table cells; html-to-docx drops nested
  // block divs (and their text) when building a <td>.
  flattenTableCellDivsForDocx(el);
  applyDocxFontSize(el, docxFontPx);

  // Convert/remove images html-to-docx can't size (webpack URLs, SVG, empty src)
  const bodyHtml = await sanitizeHtmlImagesForDocx(el.innerHTML);

  const htmlString = `<!DOCTYPE html><html><head><meta charset="UTF-8" /></head><body style="font-size:${docxFontPx}px;font-family:'Times New Roman',serif;line-height:1.35;">${bodyHtml}</body></html>`;

  const headerHTMLString = `
    <div style="display:flex;justify-content:space-between;align-items:center;width:100%;">
    </div>
  `;

  const footerHTMLString = `
    <p></p>
  `;

  const documentOptions = {
    orientation: 'portrait' as const,
    margins: {
      top: 720,
      right: 720,
      bottom: 720,
      left: 720,
      header: 360,
      footer: 360,
      gutter: 0,
    },
    title,
    header: true,
    footer: true,
    pageNumber: false,
    font: 'Times New Roman',
    fontSize: docxFontHip,
    complexScriptFontSize: docxFontHip,
  };

  const fileBuffer = await HTMLtoDOCX(
    htmlString,
    headerHTMLString,
    documentOptions,
    footerHTMLString
  );

  const rawBlob =
    fileBuffer instanceof Blob
      ? fileBuffer
      : new Blob([fileBuffer as unknown as BlobPart], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });

  // Fix empty extents, sectPr order, and vertically center inline math
  const blob = await repairDocxForWord(rawBlob, docxFontPx);

  downloadBlob(blob, filename);
};
